import { Getters } from '@/shared/app/model/store/getters';
import Slots from '@/shared/app/model/templating/Slots';
import Template from '@/shared/app/model/templating/Template';
import Dataset from '@/shared/data/model/Dataset';
import { DishesGroupConfig } from '../components/dishesGroup/ui/types';
import { activeDishesIndexesInDataset } from '../data-tools/whats-active';
import { MakeUniAccueilStandOptions, makeUniAccueilStand } from './accueil-stand.maker';

export interface MakeUniAccueilOptions {
  stands: (Omit<MakeUniAccueilStandOptions, 'originsIcons' | 'datasetName'> & {
    datasetName?: string;
    noDishesMessage?: string;
  })[];
  minNumberOfStandsInFlex?: number;
  keepStandsWithNoActiveDishes?: boolean;
  originsIcons: Dataset;
  backgroundColor: string;
  backgroundColorValue?: string;
  backgroundImage?: string;
  sizeFactor?: number;
  borderRadius?: string;
  borderWidth?: string;
  boxPadding?: string;
  padding?: string;
  dishesDatasetsPrefix?: string;
  getters?: Getters;
  dishesGroupConfig?: DishesGroupConfig;
  dishConfig?: Dataset & {
    sizeFactor?: number;
    padding?: string;
    priceTextColor?: string;
    priceBackgroundColor?: string;
    priceNoBackground?: boolean;
    lowerSizeFactor?: string;
    centerContentInUpper?: boolean;
  };
}

export const makeUniAccueil = (opts: MakeUniAccueilOptions): Template => {
  const slots: Slots = {};

  const values = {
    radius: opts.borderRadius || '0.6rem',
    borderWidth: opts.borderWidth || '0',
    padding: opts.padding || '1rem',
    backgroundColorValue: opts.backgroundColorValue || 'white',
  };

  const filteredStands = [];

  // Filtrage des stands qui n'ont pas d'offre active
  // Ajout des datasets
  for (let i = 0; i < opts.stands.length; i += 1) {
    const standConfig = opts.stands[i];
    // Ajout noms datasets
    if (!standConfig.datasetName) standConfig.datasetName = `dishes${i + 1}`;

    // Filtre stands actifs (avec plats actifs)
    // si opts.getters est défini
    if (opts.getters && !opts.keepStandsWithNoActiveDishes) {
      const activeDishesIndexes = activeDishesIndexesInDataset(
        opts.getters,
        standConfig.datasetName,
      );
      if (activeDishesIndexes.length) filteredStands.push(standConfig);
    } else {
      // sinon on ajoute tous les stands
      filteredStands.push(standConfig);
    }
  }

  const flexStands = {
    basis: opts.minNumberOfStandsInFlex
      ? `${100 / Math.max(filteredStands.length, opts.minNumberOfStandsInFlex)}%`
      : '0',
    grow: opts.minNumberOfStandsInFlex ? '0' : '1',
  };

  // Calcul du facteur de taille selon le nombre de stands actifs
  const sizeFactor = (opts.sizeFactor || 1) + (opts.stands.length - filteredStands.length) * 0.09;

  filteredStands.forEach((standConfig, i) => {
    slots[`s${i}`] = makeUniAccueilStand({
      datasetName: standConfig.datasetName!,
      headerImage: standConfig.headerImage,
      headerImageBackgroundSize: standConfig.headerImageBackgroundSize,
      headerImageBasis: standConfig.headerImageBasis,
      originsIcons: opts.originsIcons,
      sizeFactor,
      borderRadius: values.radius,
      borderWidth: values.borderWidth,
      backgroundColorValue: values.backgroundColorValue,
      padding: values.padding,
      dishGroupConfig: opts.dishesGroupConfig,
      dishConfig: opts.dishConfig,
      sides: standConfig.sides,
      waitingtime: standConfig.waitingtime,
      getters: opts.getters,
    });
  });

  return {
    component: 'Box',
    config: {
      background: true,
      backgroundColorValue: opts.backgroundColor,
      ...(opts.backgroundImage
        ? {
            backgroundImage: true,
            backgroundImagePath: opts.backgroundImage,
          }
        : {}),
      padding: opts.boxPadding || '1.4em',
    },
    slots: {
      ext: {
        component: 'AutoFlex',
        config: {
          grow: flexStands.grow,
          basis: flexStands.basis,
          shrink: '0',
          vertical: true,
          spaceBetween: '1.5rem',
        },
        slots: {
          ...slots,
        },
      },
    },
  };
};
